import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React from 'react';
import {useParams} from 'react-router-dom';

import {InfoCard} from 'src/design/molecules/InfoCard';
import {
  CalendarIcon,
  IconSize,
  InfoIcon,
  ServerIcon,
  UserIcon,
} from 'src/design/atoms/icons/CommonIcons';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {InstanceAdministratorList} from 'src/app/permissions/InstanceAdministratorList';
import {SidebarLinkAccounts} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetAccountQuery} from 'src/redux/queries/account';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

export const ViewAccountPage: React.FC = () => {
  useSetDocumentTitle('View Account');
  const {id} = useParams();
  const {data, isLoading} = useGetAccountQuery(Number(id), {
    skip: id === undefined,
  });

  if (isLoading) {
    return (
      <ContentWrapper>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <Stack gap="lg">
        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkAccounts.icon}
            subtitle={data?.email}>
            {data?.email ?? `Account #${id}`}
          </PageHeader.Title>
          <PageHeader.Badges>
            {data?.isSuperAdmin && (
              <PageHeader.Badge color="green" variant="light">
                Super Admin
              </PageHeader.Badge>
            )}
          </PageHeader.Badges>
        </PageHeader>

        <InfoCard
          title="Account Details"
          icon={<UserIcon size={IconSize.sm} />}
          compact>
          <InfoCard.Row label="Email" value={data?.email} />
          <InfoCard.Row
            icon={<InfoIcon size={IconSize.sm} />}
            label="Super Admin"
            value={data?.isSuperAdmin ? 'Yes' : 'No'}
          />
          <InfoCard.Row
            icon={<ServerIcon size={IconSize.sm} />}
            label="Primary Instance"
            value={data?.primaryInstanceName ?? `Instance ${data?.primaryInstanceId}`}
          />
          <InfoCard.Row
            label="Current Instance ID"
            value={data?.currentInstanceId}
          />
          <InfoCard.Row
            icon={<CalendarIcon size={IconSize.sm} />}
            label="Email Verified At"
            value={
              data?.emailVerifiedAt
                ? parseUtcDate(data.emailVerifiedAt).toLocaleString()
                : 'Not verified'
            }
          />
          <InfoCard.Row
            icon={<CalendarIcon size={IconSize.sm} />}
            label="Last Active At"
            value={
              data?.lastActiveAt
                ? parseUtcDate(data.lastActiveAt).toLocaleString()
                : '—'
            }
          />
          <InfoCard.Row
            icon={<CalendarIcon size={IconSize.sm} />}
            label="Created At"
            value={
              data?.createdAt
                ? parseUtcDate(data.createdAt).toLocaleString()
                : '—'
            }
          />
          <InfoCard.Row
            icon={<CalendarIcon size={IconSize.sm} />}
            label="Updated At"
            value={
              data?.updatedAt
                ? parseUtcDate(data.updatedAt).toLocaleString()
                : '—'
            }
          />
        </InfoCard>

        <InstanceAdministratorList accountId={Number(id)} readOnly />
      </Stack>
    </ContentWrapper>
  );
};

export default ViewAccountPage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.AccountsView(':id'),
  element: <ViewAccountPage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
